import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { QRCodeSVG } from 'qrcode.react';
import { initiateMfaSetup, verifyMfaSetup } from '../MfaApi';

const btnStyle = { bgcolor: '#01070B', '&:hover': { bgcolor: '#2c2c2c' } };
const linkStyle = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: '#666',
  cursor: 'pointer',
  textDecoration: 'underline',
  font: 'inherit',
  fontSize: '0.875rem',
};

const METHODS = [
  { value: 'totp', label: 'Authenticator app', desc: 'Use an app like Google Authenticator or Authy to generate codes.' },
  { value: 'email', label: 'Email', desc: 'Receive a one-time code at your email address each time you log in.' },
];

function MfaSetupWizard({ token, onEnrolled, onCancel }) {
  const [step, setStep] = useState('select-method');
  const [method, setMethod] = useState('email');
  const [qrUri, setQrUri] = useState(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const [copied, setCopied] = useState(false);

  const initiateSetup = async (selectedMethod) => {
    setSetupLoading(true);
    setError(null);
    try {
      const result = await initiateMfaSetup(token, selectedMethod);
      if (selectedMethod === 'totp' && result.qrUri) setQrUri(result.qrUri);
      if (selectedMethod === 'email') setResendCountdown(30);
    } catch (err) {
      if (err.status === 429) {
        setError('A code was sent recently. Please wait before requesting a new one.');
        if (selectedMethod === 'email') setResendCountdown(30);
      } else {
        setError('Failed to start setup. Please try again.');
      }
    } finally {
      setSetupLoading(false);
    }
  };

  const handleMethodContinue = async () => {
    setCode('');
    setError(null);
    setQrUri(null);
    await initiateSetup(method);
    setStep('enter-code');
  };

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCountdown]);

  const handleVerify = async () => {
    if (!code) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await verifyMfaSetup(token, code);
      setBackupCodes(result.backupCodes || []);
      setStep('backup-codes');
    } catch (err) {
      if (err?.status === 423) {
        onCancel();
        return;
      }
      setError('Incorrect code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyAll = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard not available
    }
  };

  const containerStyle = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 3,
    bgcolor: '#f5f5f5',
  };

  const cardStyle = {
    width: '100%',
    maxWidth: 440,
    bgcolor: '#fff',
    borderRadius: 2,
    padding: 4,
    boxShadow: 1,
  };

  const gridStyle = {
    bgcolor: '#f5f5f5',
    borderRadius: 1,
    padding: 2,
    marginBottom: 2,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 1,
  };

  return (
    <Box sx={containerStyle}>
      <Box sx={cardStyle}>
        {step === 'select-method' && (
          <>
            <Typography variant="h5" fontWeight={700} mb={1}>
              Set up Multi-Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Choose how you&apos;d like to receive your verification codes.
            </Typography>

            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              mb: 3,
            }}
            >
              {METHODS.map((m) => (
                <Box
                  key={m.value}
                  onClick={() => setMethod(m.value)}
                  data-testid={`mfa-method-${m.value}`}
                  sx={{
                    border: '2px solid',
                    borderColor: method === m.value ? '#01070B' : 'divider',
                    borderRadius: 1,
                    p: 2,
                    cursor: 'pointer',
                    bgcolor: method === m.value ? 'action.selected' : 'transparent',
                  }}
                >
                  <Typography fontWeight={method === m.value ? 700 : 400}>{m.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{m.desc}</Typography>
                </Box>
              ))}
            </Box>

            <Button
              variant="contained"
              onClick={handleMethodContinue}
              disabled={setupLoading}
              fullWidth
              data-testid="mfa-continue-btn"
              sx={btnStyle}
            >
              {setupLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Continue'}
            </Button>

            <Box mt={2} textAlign="center">
              <button type="button" style={linkStyle} data-testid="mfa-wizard-cancel-btn" onClick={onCancel}>
                Cancel and sign out
              </button>
            </Box>
          </>
        )}

        {step === 'enter-code' && (
          <>
            <Typography variant="h5" fontWeight={700} mb={1}>
              {method === 'totp' ? 'Scan the QR code' : 'Check your email'}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              {method === 'totp'
                ? 'Open your authenticator app, scan the code below, then enter the 6-digit code it generates.'
                : 'We sent a verification code to your email. Enter it below to complete setup.'}
            </Typography>

            {setupLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {method === 'totp' && qrUri && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                    <QRCodeSVG value={qrUri} size={180} data-testid="totp-qr-code" />
                  </Box>
                )}

                <TextField
                  label="6-digit code"
                  type="tel"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  fullWidth
                  error={!!error}
                  helperText={error ?? ' '}
                  inputProps={{ maxLength: 6, 'data-testid': 'mfa-verification-code-input' }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleVerify(); }}
                  autoFocus
                />
                <Button
                  variant="contained"
                  onClick={handleVerify}
                  disabled={submitting || code.length < 6}
                  data-testid="mfa-verify-btn"
                  sx={btnStyle}
                >
                  {submitting
                    ? <CircularProgress size={20} sx={{ color: '#fff' }} />
                    : 'Verify'}
                </Button>

                {method === 'email' && (
                  <Typography variant="body2" textAlign="center">
                    Didn&apos;t receive it?&nbsp;
                    <button
                      type="button"
                      style={{ ...linkStyle, color: resendCountdown > 0 ? '#aaa' : '#0026A7' }}
                      data-testid="mfa-setup-resend-btn"
                      onClick={() => initiateSetup('email')}
                      disabled={resendCountdown > 0}
                    >
                      {resendCountdown > 0 ? `Resend (${resendCountdown}s)` : 'Resend'}
                    </button>
                  </Typography>
                )}
              </Box>
            )}

            <Box mt={3} textAlign="center">
              <button type="button" style={linkStyle} data-testid="mfa-setup-back-btn" onClick={() => setStep('select-method')}>
                Back
              </button>
              {' · '}
              <button type="button" style={linkStyle} data-testid="mfa-wizard-cancel-btn" onClick={onCancel}>
                Cancel and sign out
              </button>
            </Box>
          </>
        )}

        {step === 'backup-codes' && (
          <>
            <Typography variant="h5" fontWeight={700} mb={1}>
              Save your backup codes
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Save these backup codes in a safe place. Each code can be used once
              to sign in if you lose access to your verification method.
            </Typography>

            <Box sx={gridStyle}>
              {backupCodes.map((c) => (
                <Typography
                  key={c}
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    letterSpacing: '0.05em',
                  }}
                >
                  {c}
                </Typography>
              ))}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCopyAll}
                data-testid="mfa-copy-all-btn"
                sx={{ borderColor: '#01070B', color: '#01070B' }}
              >
                {copied ? 'Copied!' : 'Copy All'}
              </Button>
              <Button variant="contained" onClick={onEnrolled} data-testid="mfa-done-btn" sx={btnStyle}>
                Done, I&apos;ve saved them
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

MfaSetupWizard.propTypes = {
  token: PropTypes.string.isRequired,
  onEnrolled: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default MfaSetupWizard;
